import React, { useState, useEffect } from 'react';
import './App.css';
import AppHeader from './components/AppHeader';
import ContentRenderer from './components/ContentRenderer';
import { useAudioManager } from './hooks/useAudioManager';
import { useServerHealth } from './hooks/useServerHealth';
import { useGameState } from './hooks/useGameState';
import authService from './services/authService';

type AppState = 'login' | 'register' | 'menu' | 'in_queue' | 'in_lobby' | 'in_game';

function App() {
  const { serverStatus, isLoading, error } = useServerHealth();
  const audioManager = useAudioManager();
  const [volume, setVolume] = useState(audioManager.getVolume());
  const [isMuted, setIsMuted] = useState(audioManager.getIsMuted());
  const [userToken, setUserToken] = useState<string | null>(authService.getCurrentUserToken());
  const [appState, setAppState] = useState<AppState>('login');

  const {
    playerId,
    gameId,
    lobbyPlayers,
    gameState,
    marketCards,
    handleFindGame,
    handleCancelFindGame,
    handlePlayCard,
  } = useGameState(userToken);

  // Update app state based on user token
  useEffect(() => {
    if (userToken) {
      setAppState('menu');
    } else {
      setAppState('login');
    }
  }, [userToken]);

  // Handle queue status updates
  useEffect(() => {
    if (!userToken) return;

    const handleQueueStatus = (message: any) => {
      if (message.type === 'queue_status') {
        setAppState(message.status === 'in_queue' ? 'in_queue' : 'menu');
      }
    };

    // This would be handled by the socket service in a real implementation
    // For now, we'll keep it simple
  }, [userToken]);

  const handleHeal = () => {
    console.log("Heal action triggered");
    // Implement actual heal logic here, e.g., via socketService
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value);
    setVolume(newVolume);
    audioManager.setVolume(newVolume);
  };

  const handleMuteToggle = () => {
    audioManager.toggleMute();
    setIsMuted(audioManager.getIsMuted());
  };

  const handleLoginSuccess = (token: string) => setUserToken(token);
  
  const handleLogout = () => {
    authService.logout();
    setUserToken(null);
    setAppState('login');
  };
  
  const handleRegisterSuccess = () => setAppState('login');

  if (isLoading) {
    return <div className="App">Loading...</div>;
  }

  if (error) {
    return <div className="App">Error: {error}</div>;
  }

  return (
    <div className="App">
      <AppHeader
        serverStatus={serverStatus}
        volume={volume}
        isMuted={isMuted}
        onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
      />
      
      <div className="game-section">
        <ContentRenderer
          appState={appState}
          playerId={playerId}
          gameId={gameId}
          lobbyPlayers={lobbyPlayers}
          gameState={gameState}
          marketCards={marketCards}
          onLoginSuccess={handleLoginSuccess}
          onRegisterSuccess={handleRegisterSuccess}
          onLogout={handleLogout}
          onFindGame={handleFindGame}
          onCancelFindGame={handleCancelFindGame}
          onPlayCard={handlePlayCard}
          onHeal={handleHeal}
          setAppState={setAppState}
        />
      </div>
    </div>
  );
}

export default App;